import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {authHeader, getStoredUser, logout, readApiJson} from '../api/auth';
import {API_BASE_URL} from '../config';
import './Login.css';

async function request(path: string, data?: object) {
  const headers: Record<string, string> = {...authHeader(), 'Content-Type':'application/json'};
  const response = await fetch(`${API_BASE_URL}/api/account${path}`, {
    method:data ? 'POST' : 'GET', headers, ...(data ? {body:JSON.stringify(data)} : {})
  });
  return readApiJson(response, headers.Authorization);
}
export default function Account({recovery = false}: {recovery?: boolean}) {
  const [email,setEmail] = useState(getStoredUser()?.username || '');
  const [currentPassword,setCurrent] = useState('');
  const [newPassword,setNew] = useState('');
  const [confirm,setConfirm] = useState('');
  const [code,setCode] = useState('');
  const [codes,setCodes] = useState<string[]>([]);
  const [remaining,setRemaining] = useState<number | null>(null);
  const [error,setError] = useState('');
  const [message,setMessage] = useState('');
  const [busy,setBusy] = useState(false);
  useEffect(() => {
    if (!recovery) request('').then(data => {setEmail(data.username);setRemaining(data.recoveryCodesRemaining);}).catch(e=>setError(e.message));
  }, [recovery]);
  const change = async (event: React.FormEvent) => {
    event.preventDefault();setError('');setMessage('');
    if (newPassword !== confirm) {setError('The new passwords do not match.');return;}
    setBusy(true);
    try {
      const result = await request(recovery ? '/recover' : '/password', recovery ? {username:email,code,newPassword} : {currentPassword,newPassword});
      setCurrent('');setNew('');setConfirm('');setCode('');setCodes([]);
      await logout();
      // Full navigation clears all authenticated state and prevents accidental
      // use of an old token after the server revokes every human session.
      window.location.assign('/?passwordChanged=1');
      setMessage(result.message);
    } catch (e:any) {setError(e.message);} finally {setBusy(false);}
  };
  const generate = async () => {
    setBusy(true);setError('');setMessage('');
    try {const result = await request('/recovery-codes',{currentPassword});setCodes(result.codes);setRemaining(result.codes.length);setCurrent('');}
    catch(e:any){setError(e.message);}finally{setBusy(false);}
  };
  const download = () => {
    const blob = new Blob([`Betbuilder recovery codes for ${email}\nKeep these private. A password reset invalidates the entire set.\n\n${codes.join('\n')}\n`],{type:'text/plain'});
    const url = URL.createObjectURL(blob);const a=document.createElement('a');a.href=url;a.download='betbuilder-recovery-codes.txt';a.click();setTimeout(()=>URL.revokeObjectURL(url),1000);
  };
  return <div className="login-shell"><section className="login-card" style={{maxWidth:560,width:'100%'}}>
    <h1>{recovery ? 'Recover your account' : 'Account settings'}</h1>
    <p>{recovery ? 'Enter your email and one of your saved recovery codes to set a new password.' : email}</p>
    {error && <p className="login-error" role="alert">{error}</p>}
    {message && <p role="status">{message}</p>}
    <form className="login-form" onSubmit={change}>
      {recovery ? <>
        <label className="login-field">Email<input type="email" autoComplete="username" required value={email} onChange={e=>setEmail(e.target.value)} /></label>
        <label className="login-field">Recovery code<input autoComplete="off" required value={code} onChange={e=>setCode(e.target.value)} /></label>
      </> : <label className="login-field">Current password<input type="password" autoComplete="current-password" required maxLength={256} value={currentPassword} onChange={e=>setCurrent(e.target.value)} /></label>}
      <label className="login-field">New password<input type="password" autoComplete="new-password" required minLength={15} maxLength={256} value={newPassword} onChange={e=>setNew(e.target.value)} /></label>
      <small>Use 15–256 characters. A long, unique passphrase works well.</small>
      <label className="login-field">Confirm new password<input type="password" autoComplete="new-password" required minLength={15} maxLength={256} value={confirm} onChange={e=>setConfirm(e.target.value)} /></label>
      <p>Changing your password signs out all browser sessions and invalidates old recovery codes. Automated racing services continue running.</p>
      <button className="login-submit" disabled={busy}>{busy ? 'Please wait…' : recovery ? 'Reset password' : 'Change password'}</button>
    </form>
    {!recovery && <section style={{marginTop:28}}>
      <h2>Account recovery</h2><p>{remaining === null ? 'Checking recovery codes…' : `${remaining} recovery codes saved.`} Save codes somewhere private before you need them.</p>
      <p>Enter your current password above, then generate codes. New codes replace your previous set. They are shown only once.</p>
      <button type="button" disabled={busy || !currentPassword} onClick={generate}>Generate recovery codes</button>
      {codes.length > 0 && <div role="status"><pre style={{overflowX:'auto',fontSize:12}}>{codes.join('\n')}</pre><button type="button" onClick={download}>Download codes</button><button type="button" onClick={()=>setCodes([])}>I have saved them</button></div>}
    </section>}
    {recovery && <><p>If you have no saved code, contact the site owner for an identity-verified reset. Email reset links are not enabled.</p><Link to="/">Back to sign in</Link></>}
  </section></div>;
}
